import React, { useRef, useState } from 'react'

import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import ButtonGroup from '@mui/material/ButtonGroup'
import type { ButtonGroupProps } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

export type SplitButtonMenuItem = {
	label: string
	onClick?: () => void
}

export type SplitButtonProps = {
	label: string
	onClick?: () => void
	menuItems: SplitButtonMenuItem[]
	buttonGroupProps?: ButtonGroupProps
}

const SplitButton = ({
	label,
	onClick,
	menuItems,
	buttonGroupProps,
}: SplitButtonProps) => {
	const anchorRef = useRef<HTMLDivElement>(null)

	const [open, setOpen] = useState(false)

	const hasMenuItems = Boolean(menuItems[0])

	const handleToggleOpen = () => {
		setOpen((prevOpen) => !prevOpen)
	}

	const handleClose = (event: Event) => {
		if (anchorRef?.current?.contains(event.target as HTMLElement)) {
			return
		}

		setOpen(false)
	}

	return (
		<>
			<ButtonGroup
				variant="contained"
				ref={anchorRef}
				{...buttonGroupProps}
			>
				<Button onClick={onClick} sx={{ textTransform: 'none' }}>
					{label}
				</Button>
				{hasMenuItems && (
					<Button
						size="small"
						aria-controls={open ? 'split-button-menu' : undefined}
						aria-expanded={open ? 'true' : undefined}
						aria-haspopup="menu"
						onClick={handleToggleOpen}
					>
						<ArrowDropDownIcon />
					</Button>
				)}
			</ButtonGroup>
			<Popper
				sx={{ zIndex: 1 }}
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				placement="bottom-end"
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin:
								placement === 'bottom'
									? 'center top'
									: 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{menuItems.map((item) => (
										<MenuItem key={item.label} onClick={item.onClick}>
											{item.label}
										</MenuItem>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	)
}

export default SplitButton
